import React, { useEffect, useMemo, useRef, useState } from "react";
import "./Styles.css";
import HeaderToMainPage from "../../Universal/Header/Index";
import Footer from "../../Universal/Footer/Index";
import { useWindowWidth } from "@react-hook/window-size";
import { Link } from "react-router-dom";
import {
    AnimatePresence,
    motion,
    useReducedMotion,
    useScroll,
    useSpring,
    useTransform
} from "framer-motion";
import {
    Activity,
    ArrowRight,
    BadgeCheck,
    Banknote,
    Bot,
    Boxes,
    Braces,
    Check,
    ChevronDown,
    Database,
    Fingerprint,
    Gamepad2,
    Gauge,
    Globe2,
    KeyRound,
    Layers3,
    LockKeyhole,
    Network,
    Play,
    Radio,
    Rocket,
    Route,
    Scale,
    Search,
    ServerCog,
    ShieldCheck,
    Sparkles,
    TerminalSquare,
    UsersRound,
    WalletCards,
    Waypoints,
    Zap
} from "lucide-react";

const platformModules = [
    {
        id: "brand",
        number: "01",
        label: "POSITIONING & STOREFRONT",
        title: "Make the first visit answer the right questions.",
        short: "Introduce the offer clearly: who the casino serves, what players can try, how the account works and where the business may operate.",
        body: "A startup needs more than attractive branding. Use the configurable storefront to explain the product, surface a deliberate launch catalog and guide visitors from discovery to account access. Test the message with real prospects before spending heavily on acquisition.",
        bullets: [
            "Brand story, homepage copy and navigation",
            "Clear player registration and demo pathways",
            "Responsive content across desktop and mobile",
            "Launch messaging informed by your intended market",
        ],
        telemetry: ["AUDIENCE / DEFINED", "STOREFRONT / CONFIGURABLE", "FUNNEL / TESTABLE"],
        icon: Globe2,
        accent: "cyan"
    },
    {
        id: "games",
        number: "02",
        label: "LAUNCH CATALOG STRATEGY",
        title: "Launch a focused catalog before chasing every provider.",
        short: "Choose a first collection of recognizable games and distinctive originals that fits the audience you actually plan to attract.",
        body: "A new operator can lead with familiar table experiences, quick-play originals and selected interactive titles rather than trying to license every studio immediately. Ethosino gives you existing first-party content to test player interest; provider expansion should follow observed demand and commercial fit.",
        bullets: [
            "Curate titles for the initial target audience",
            "Balance familiar games with differentiated originals",
            "Evaluate engagement before expanding categories",
            "Discuss additional providers as a later decision",
        ],
        telemetry: ["CATALOG / CURATED", "DEMAND / VALIDATED", "EXPANSION / OPTIONAL"],
        icon: Gamepad2,
        accent: "violet"
    },
    {
        id: "money",
        number: "03",
        label: "PAYMENTS & CASH-FLOW",
        title: "Plan how money enters, moves and leaves the business.",
        short: "Payment acceptance, redemption operations and reconciliation belong in the launch budget—not in the list of things to fix later.",
        body: "Before inviting production players, confirm what payment and identity partners the operator can actually use. Define review responsibilities, funding requirements, redemption procedures and support ownership. The software provides operational workflows, but external merchant approval and provider readiness remain real launch dependencies.",
        bullets: [
            "Select and qualify payment partners",
            "Define redemption and payout review procedures",
            "Plan liquidity and reconciliation responsibilities",
            "Test edge cases before expanding volume",
        ],
        telemetry: ["PROVIDERS / QUALIFIED", "REDEMPTIONS / PLANNED", "CASH-FLOW / REVIEWED"],
        icon: WalletCards,
        accent: "green"
    },
    {
        id: "trust",
        number: "04",
        label: "MARKET ENTRY READINESS",
        title: "Know the launch conditions before taking deposits.",
        short: "The target jurisdictions, sweepstakes rules, identity checks and player disclosures must be decided before production traffic.",
        body: "Ethosino can provide software controls for permitted access and operational review, but it does not supply a legal opinion or create payment-provider approval. Work with appropriate counsel and providers to settle the operator model, required notices, identity verification and market restrictions early enough to affect the launch plan.",
        bullets: [
            "Identify intended markets and exclusions",
            "Budget for counsel and required approvals",
            "Confirm identity and verification partners",
            "Prepare player disclosures and support policies",
        ],
        telemetry: ["MARKETS / SELECTED", "COUNSEL / REQUIRED", "PARTNERS / REVIEWED"],
        icon: ShieldCheck,
        accent: "amber"
    },
    {
        id: "operations",
        number: "05",
        label: "SMALL-TEAM OPERATIONS",
        title: "Design the first week of work, not just launch day.",
        short: "Decide who handles player questions, redemptions, exceptions, promotions and technical issues when the first real users arrive.",
        body: "A founder-led team needs a practical daily routine: review incoming activity, prioritize support, resolve exceptions, and know when to escalate a payment or gameplay issue. Existing administrative screens shorten the distance between noticing a problem and assigning a human response.",
        bullets: [
            "Assign ownership for daily operator tasks",
            "Prepare support and escalation runbooks",
            "Review exceptions and pending requests",
            "Establish an operating checklist for each day",
        ],
        telemetry: ["RESPONSIBILITY / ASSIGNED", "SUPPORT / PREPARED", "WORKFLOW / REPEATABLE"],
        icon: ServerCog,
        accent: "blue"
    },
    {
        id: "copilot",
        number: "06",
        label: "DECISION SUPPORT",
        title: "Use early data to decide what deserves investment.",
        short: "Track which games attract interest, where onboarding stalls and whether support or redemption volume is outpacing the team.",
        body: "The startup challenge is making decisions with incomplete evidence. Ethosino's advisory tools can help an authorized operator examine operational signals and frame follow-up questions. They do not replace experienced judgment, reconcile missing data automatically or make financial decisions on the operator's behalf.",
        bullets: [
            "Review acquisition-to-play indicators",
            "Spot changes in activity and exception volume",
            "Turn observations into weekly priorities",
            "Keep final decisions with the operating team",
        ],
        telemetry: ["SIGNALS / REVIEWED", "QUESTIONS / PRIORITIZED", "DECISIONS / HUMAN"],
        icon: Bot,
        accent: "pink"
    }
];

const startupPain = [
    {
        number: "01",
        title: "The audience cannot be an afterthought.",
        text: "A polished casino will not generate distribution by itself. Start with a defined customer, a credible acquisition channel and a clear answer to why somebody would return.",
        icon: Braces,
        accent: "cyan"
    },
    {
        number: "02",
        title: "External approvals can decide your launch date.",
        text: "Legal review, acceptable payment methods, identity verification and redemption arrangements can have longer lead times than software setup. Qualify them before announcing a go-live date.",
        icon: Waypoints,
        accent: "violet"
    },
    {
        number: "03",
        title: "Your first hundred players will expose the real workload.",
        text: "Support queues, unanswered questions, payment exceptions and retention signals reveal what the business needs next. A small launch should create learning—not unmanageable obligations.",
        icon: Rocket,
        accent: "green"
    }
];

const evidenceModes = [
    {
        id: "fairness",
        label: "GAME ACCEPTANCE",
        icon: Fingerprint,
        accent: "cyan",
        title: "Test the games you intend to offer first.",
        copy: "Review a sample acceptance checklist for a launch game: player flow, round completion, visible outcomes, a usable game history and a clear escalation path. This is an illustrative checklist, not a live verification result.",
        rows: [
            ["selected_game", "Mines / example", "SAMPLE"],
            ["account_flow", "demo account", "CHECK"],
            ["session_result", "visible to player", "CHECK"],
            ["round_history", "reviewable", "CHECK"],
            ["launch_status", "pending sign-off", "REVIEW"]
        ]
    },
    {
        id: "ledger",
        label: "PAYOUT READINESS",
        icon: Banknote,
        accent: "green",
        title: "Know who owns the first payout exception.",
        copy: "Before opening a production cashier, agree on provider approval, redemption processing, supporting documents, treasury responsibilities and failure handling. The sample fields below are planning examples.",
        rows: [
            ["payment_provider", "selection pending", "PLAN"],
            ["redemption_policy", "operator approval", "REVIEW"],
            ["treasury_owner", "assigned role", "PLAN"],
            ["exception_queue", "support + finance", "PLAN"],
            ["launch_status", "integration testing", "CHECK"]
        ]
    },
    {
        id: "audit",
        label: "SUPPORT READINESS",
        icon: Database,
        accent: "amber",
        title: "Give the first support agent a sensible escalation path.",
        copy: "A small team needs to know how to classify a reported problem, locate the relevant account context, document the response and pass sensitive requests to the right person. These are example workflow fields.",
        rows: [
            ["case_type", "missing redemption", "SAMPLE"],
            ["first_response", "player support", "ASSIGN"],
            ["escalation", "finance review", "ASSIGN"],
            ["resolution_note", "required", "CHECK"],
            ["case_state", "awaiting review", "EXAMPLE"]
        ]
    },
    {
        id: "copilot",
        label: "WEEKLY REVIEW",
        icon: Bot,
        accent: "pink",
        title: "Turn your first week into decisions for the second.",
        copy: "Compare visits, registrations, first plays and support demand. Agree on what constitutes a meaningful signal before spending more on traffic. The example below is illustrative, not a live report.",
        rows: [
            ["visitors", "tracked sessions", "MEASURE"],
            ["signups", "completed accounts", "MEASURE"],
            ["first_play", "activated players", "MEASURE"],
            ["support_load", "requests per day", "MEASURE"],
            ["next_action", "one prioritized test", "DECIDE"]
        ]
    }
];

const launchStages = [
    {
        number: "01",
        kicker: "DEFINE",
        title: "Choose the audience, format and first offer.",
        copy: "Write a market brief: target users and jurisdictions, intended game mix, acquisition channel, player support commitments and budget. Identify the assumptions that need outside confirmation.",
        icon: Layers3
    },
    {
        number: "02",
        kicker: "QUALIFY",
        title: "Clear the partners and approvals on your critical path.",
        copy: "Consult counsel on the intended sweepstakes model, begin payment-provider conversations and select identity and other required services. Approval and integration timelines are external dependencies.",
        icon: Network
    },
    {
        number: "03",
        kicker: "PILOT",
        title: "Run a small, measurable operator test.",
        copy: "Configure an initial brand and content set, exercise accounts and games, verify operational workflows, and establish acceptance criteria. Expand only when the observed results and outstanding issues justify it.",
        icon: Gauge
    },
    {
        number: "04",
        kicker: "DECIDE",
        title: "Review evidence before committing to the next phase.",
        copy: "Assess activation, repeat engagement, support workload, redemption performance and cost of acquisition. Choose whether to improve, expand, change the offer or stop—based on what the pilot taught you.",
        icon: Route
    }
];

const comparisonRows = [
    ["Initial build", "Highest upfront engineering effort", "Lower software setup; contract-dependent", "Existing software plus operator-specific setup"],
    ["Launch ownership", "Founder must staff engineering and operations", "Services and flexibility depend on vendor", "Founder owns audience, marketing and business decisions"],
    ["Game selection", "Design or source each title", "Depends on package and supplier agreements", "Curate available first-party games for the pilot"],
    ["External approvals", "Operator must arrange them", "Operator obligations vary by agreement", "Legal, payments and identity remain operator tasks"],
    ["First-month learning", "Custom instrumentation to build", "Available metrics vary by provider", "Pilot measures activation, play and service workload"],
    ["Change after pilot", "New build work can be costly", "Check contractual scope and limitations", "Review additions with defined commercial scope"]
];

const faqItems = [
    {
        q: "Who is the Startup Operators solution intended for?",
        a: "Founders, small teams and established businesses testing a first branded sweepstakes-style gaming offering. The best fit is an operator with an audience or credible distribution strategy, a defined target market and the ability to fund legal and payment-provider work."
    },
    {
        q: "Does Ethosino bring customers or run our marketing?",
        a: "No. Ethosino supplies a software platform, not a player audience or an acquisition guarantee. The operator is responsible for positioning, advertising, player communications, retention strategy and measuring the economics of acquiring customers."
    },
    {
        q: "What do we need before a pilot can begin?",
        a: "A proposed brand, intended markets, an initial product plan, named operating contacts and an agreed pilot scope. Legal analysis and provider selection should begin early. Some software exploration can happen before production integrations, but a live commercial launch has additional requirements."
    },
    {
        q: "Can we use the platform without a payment provider on day one?",
        a: "A controlled demonstration can be used to evaluate software and player flows without implying production payment readiness. Accepting deposits or fulfilling redemptions requires suitable provider arrangements, testing and the operator's applicable legal posture."
    },
    {
        q: "How should we measure whether the pilot is working?",
        a: "Choose success criteria before launch: qualified visits, registration completion, first-play activation, repeat activity, support response times, payment or redemption exceptions and acquisition costs where available. Early signals are directional; they do not guarantee future revenue."
    },
    {
        q: "What is included commercially in the startup pilot?",
        a: "Scope and commercial terms should be confirmed in writing for each pilot, including included features, integrations, support, duration, operator responsibilities and what happens afterward. Do not assume a legal opinion, merchant approval or unlimited custom development is included."
    },
    {
        q: "How quickly can we launch a production casino?",
        a: "Software tenant setup can be faster than a ground-up build, but production dates depend on the intended markets, legal advice, provider onboarding, integration testing, content and the operator's readiness. A target date should be agreed only after those dependencies are assessed."
    },
    {
        q: "Can we start with a small catalog and expand later?",
        a: "Yes, a selective first-party launch catalog is a sensible starting point. Use the pilot to learn which categories attract and retain your audience, then assess additional originals or external providers based on demand, cost and integration scope."
    }
];

const FlyIn = ({
    children,
    className = "",
    direction = "bottomLeft",
    delay = 0,
    amount = 0.18
}) => {
    const reduceMotion = useReducedMotion();

    const origins = {
        left: { x: -110, y: 0, rotate: -1.5 },
        right: { x: 110, y: 0, rotate: 1.5 },
        topLeft: { x: -115, y: -85, rotate: -3 },
        topRight: { x: 115, y: -85, rotate: 3 },
        bottomLeft: { x: -115, y: 85, rotate: 3 },
        bottomRight: { x: 115, y: 85, rotate: -3 },
        up: { x: 0, y: 70, rotate: 0 }
    };

    const origin = origins[direction] || origins.bottomLeft;

    return (
        <motion.div
            className={className}
            initial={
                reduceMotion
                    ? false
                    : {
                        opacity: 0,
                        x: origin.x,
                        y: origin.y,
                        rotate: origin.rotate,
                        scale: 0.94,
                        filter: "blur(10px)"
                    }
            }
            whileInView={
                reduceMotion
                    ? {}
                    : {
                        opacity: 1,
                        x: 0,
                        y: 0,
                        rotate: 0,
                        scale: 1,
                        filter: "blur(0px)"
                    }
            }
            viewport={{ once: true, amount }}
            transition={{
                duration: 0.82,
                delay,
                ease: [0.16, 1, 0.3, 1]
            }}
        >
            {children}
        </motion.div>
    );
};

const SpotlightCard = ({ children, className = "" }) => {
    const ref = useRef(null);

    const handlePointerMove = (event) => {
        const node = ref.current;
        if (!node) return;

        const rect = node.getBoundingClientRect();
        node.style.setProperty("--es6-mx", `${event.clientX - rect.left}px`);
        node.style.setProperty("--es6-my", `${event.clientY - rect.top}px`);
    };

    return (
        <div
            ref={ref}
            className={`ethosino-startup-v6-spotlight ${className}`}
            onPointerMove={handlePointerMove}
        >
            {children}
        </div>
    );
};

const StartupOperatorsSolutionHelper = () => {
    const width = useWindowWidth();
    const reduceMotion = useReducedMotion();
    const pageRef = useRef(null);

    const [activeModule, setActiveModule] = useState("operations");
    const [activeEvidence, setActiveEvidence] = useState("fairness");
    const [activeFaq, setActiveFaq] = useState(0);
    const [copilotPrompt, setCopilotPrompt] = useState(
        "Which onboarding step lost the most new players this week?"
    );
    const [copilotRunning, setCopilotRunning] = useState(false);

    const { scrollYProgress } = useScroll({
        target: pageRef,
        offset: ["start start", "end end"]
    });

    const progress = useSpring(scrollYProgress, {
        stiffness: 115,
        damping: 26,
        restDelta: 0.001
    });

    const heroY = useTransform(
        scrollYProgress,
        [0, 0.15],
        [0, reduceMotion ? 0 : 90]
    );

    const heroScale = useTransform(
        scrollYProgress,
        [0, 0.14],
        [1, reduceMotion ? 1 : 0.965]
    );

    const selectedModule = useMemo(
        () => platformModules.find((item) => item.id === activeModule) || platformModules[0],
        [activeModule]
    );

    const selectedEvidence = useMemo(
        () => evidenceModes.find((item) => item.id === activeEvidence) || evidenceModes[0],
        [activeEvidence]
    );

    useEffect(() => {
        const handler = (event) => {
            const anchor = event.target.closest?.("a[href^='#']");
            if (!anchor) return;

            const href = anchor.getAttribute("href");
            const id = href?.slice(1);
            const section = id ? document.getElementById(id) : null;

            if (!section) return;

            event.preventDefault();

            const top = section.getBoundingClientRect().top + window.scrollY - 92;

            window.scrollTo({
                top,
                behavior: reduceMotion ? "auto" : "smooth"
            });
        };

        document.addEventListener("click", handler);
        return () => document.removeEventListener("click", handler);
    }, [reduceMotion]);

    const handlePagePointerMove = (event) => {
        if (!pageRef.current) return;

        pageRef.current.style.setProperty("--es6-pointer-x", `${event.clientX}px`);
        pageRef.current.style.setProperty("--es6-pointer-y", `${event.clientY}px`);
    };

    const runCopilot = () => {
        if (copilotRunning) return;
        setCopilotRunning(true);
        window.setTimeout(() => setCopilotRunning(false), 1150);
    };

    const SelectedModuleIcon = selectedModule.icon;
    const EvidenceIcon = selectedEvidence.icon;

    return (
        <>
            <HeaderToMainPage />
            {width <= 775 ? <div style={{ height: 87.25 }} /> : null}

            <main
                ref={pageRef}
                className="ethosino-startup-v6-page ethosino-startup-v6-readable"
                onPointerMove={handlePagePointerMove}
            >
                <motion.div
                    className="ethosino-startup-v6-progress"
                    style={{ scaleX: progress }}
                />

                <div className="ethosino-startup-v6-pointer-glow" aria-hidden="true" />

                <div className="ethosino-startup-v6-atmosphere" aria-hidden="true">
                    <div className="ethosino-startup-v6-grid" />
                    <div className="ethosino-startup-v6-scanlines" />
                    <div className="ethosino-startup-v6-orb ethosino-startup-v6-orb--one" />
                    <div className="ethosino-startup-v6-orb ethosino-startup-v6-orb--two" />
                    <div className="ethosino-startup-v6-orb ethosino-startup-v6-orb--three" />
                </div>

                {/* ==========================================================
                    HERO
                ========================================================== */}
                <section style={{ marginTop: 50 }} className="ethosino-startup-v6-hero" id="top">
                    <motion.div
                        className="ethosino-startup-v6-hero-parallax"
                        style={{ y: heroY, scale: heroScale }}
                    >
                        <div className="ethosino-startup-v6-shell ethosino-startup-v6-hero-grid">
                            <div className="ethosino-startup-v6-hero-copy">
                                <motion.div
                                    className="ethosino-startup-v6-signal-badge"
                                    initial={reduceMotion ? false : { opacity: 0, x: -45, y: -20 }}
                                    animate={reduceMotion ? {} : { opacity: 1, x: 0, y: 0 }}
                                    transition={{ duration: 0.6 }}
                                >
                                    <span className="ethosino-startup-v6-signal-dot" />
                                    FOR FIRST-TIME OPERATORS
                                    <span className="ethosino-startup-v6-signal-divider" />
                                    BUILD A LAUNCH PLAN THAT HOLDS UP
                                </motion.div>

                                <motion.h1
                                    initial={reduceMotion ? false : { opacity: 0, x: -90, y: 45, rotate: -2 }}
                                    animate={reduceMotion ? {} : { opacity: 1, x: 0, y: 0, rotate: 0 }}
                                    transition={{ duration: 0.82, delay: 0.05, ease: [0.16, 1, 0.3, 1] }}
                                >
                                    Launch the first casino thoughtfully.
                                    <span> Prove the business before you scale it.</span>
                                </motion.h1>

                                <motion.p
                                    className="ethosino-startup-v6-hero-lead"
                                    initial={reduceMotion ? false : { opacity: 0, x: -60, y: 20 }}
                                    animate={reduceMotion ? {} : { opacity: 1, x: 0, y: 0 }}
                                    transition={{ duration: 0.72, delay: 0.16 }}
                                >
                                    Ethosino helps early-stage operators test a branded casino concept with working software, an initial game catalog and an operator workspace. Instead of starting with a multi-year build, start by validating demand, defining your operating procedures and preparing the external partners required for launch.
                                </motion.p>

                                <motion.div
                                    className="ethosino-startup-v6-thesis"
                                    initial={reduceMotion ? false : { opacity: 0, x: -45 }}
                                    animate={reduceMotion ? {} : { opacity: 1, x: 0 }}
                                    transition={{ duration: 0.65, delay: 0.28 }}
                                >
                                    <Sparkles size={16} />
                                    <span>
                                        <strong>A launch is a test of an actual business.</strong>{" "}
                                        Choose the right audience, prove the player journey and learn from a limited pilot before expanding spend or scope.
                                    </span>
                                </motion.div>

                                <motion.div
                                    className="ethosino-startup-v6-hero-actions"
                                    initial={reduceMotion ? false : { opacity: 0, x: -35, y: 25 }}
                                    animate={reduceMotion ? {} : { opacity: 1, x: 0, y: 0 }}
                                    transition={{ duration: 0.65, delay: 0.36 }}
                                >
                                    <Link
                                        className="ethosino-startup-v6-btn ethosino-startup-v6-btn--primary"
                                        to="/book-demo"
                                    >
                                        <Rocket size={17} />
                                        <span>Plan a startup pilot</span>
                                        <ArrowRight size={16} />
                                    </Link>

                                    <a
                                        className="ethosino-startup-v6-btn ethosino-startup-v6-btn--ghost"
                                        href="#operator-stack"
                                    >
                                        <Play size={16} />
                                        <span>Explore startup essentials</span>
                                    </a>
                                </motion.div>

                                <motion.div
                                    className="ethosino-startup-v6-proofline"
                                    initial={reduceMotion ? false : { opacity: 0, y: 25 }}
                                    animate={reduceMotion ? {} : { opacity: 1, y: 0 }}
                                    transition={{ duration: 0.65, delay: 0.44 }}
                                >
                                    <span><Gamepad2 size={14} /> Existing launch catalog</span>
                                    <span><Fingerprint size={14} /> Practical product demonstrations</span>
                                    <span><Layers3 size={14} /> Operator setup workflows</span>
                                    <span><Bot size={14} /> Launch performance insights</span>
                                </motion.div>
                            </div>

                            <motion.aside
                                className="ethosino-startup-v6-launch-console"
                                initial={
                                    reduceMotion
                                        ? false
                                        : { opacity: 0, x: 135, y: -90, rotate: 4, scale: 0.9 }
                                }
                                animate={
                                    reduceMotion
                                        ? {}
                                        : { opacity: 1, x: 0, y: 0, rotate: 0, scale: 1 }
                                }
                                transition={{ duration: 0.95, delay: 0.12, ease: [0.16, 1, 0.3, 1] }}
                            >
                                <div className="ethosino-startup-v6-console-grid" />
                                <div className="ethosino-startup-v6-console-scan" />

                                <div className="ethosino-startup-v6-console-head">
                                    <div>
                                        <span className="ethosino-startup-v6-live-dot" />
                                        ILLUSTRATIVE / LAUNCH CHECKLIST
                                    </div>
                                    <span>SAMPLE_BRAND</span>
                                </div>

                                <div className="ethosino-startup-v6-orbit-system">
                                    <motion.div
                                        className="ethosino-startup-v6-orbit-ring ethosino-startup-v6-orbit-ring--outer"
                                        animate={reduceMotion ? {} : { rotate: 360 }}
                                        transition={{ duration: 30, repeat: Infinity, ease: "linear" }}
                                    />
                                    <motion.div
                                        className="ethosino-startup-v6-orbit-ring ethosino-startup-v6-orbit-ring--middle"
                                        animate={reduceMotion ? {} : { rotate: -360 }}
                                        transition={{ duration: 20, repeat: Infinity, ease: "linear" }}
                                    />
                                    <div className="ethosino-startup-v6-orbit-ring ethosino-startup-v6-orbit-ring--inner" />

                                    <div className="ethosino-startup-v6-core-node">
                                        <span className="ethosino-startup-v6-core-pulse" />
                                        <Boxes size={27} />
                                        <small>ETHOSINO</small>
                                        <strong>CORE</strong>
                                        <b>OPERATOR SETUP</b>
                                    </div>

                                    {[
                                        ["players", UsersRound, "AUDIENCE", "DEFINED", "cyan"],
                                        ["games", Gamepad2, "CATALOG", "CURATED", "violet"],
                                        ["money", WalletCards, "PAYMENTS", "QUALIFY", "green"],
                                        ["risk", ShieldCheck, "MARKETS", "REVIEW", "amber"],
                                        ["ops", ServerCog, "SUPPORT", "ASSIGN", "blue"],
                                        ["ai", Bot, "METRICS", "TRACK", "pink"]
                                    ].map(([id, Icon, label, value, accent], index) => (
                                        <motion.div
                                            key={id}
                                            className={`ethosino-startup-v6-orbit-card ethosino-startup-v6-orbit-card--${index + 1} ethosino-startup-v6-orbit-card--${accent}`}
                                            initial={
                                                reduceMotion
                                                    ? false
                                                    : {
                                                        opacity: 0,
                                                        x: index % 2 === 0 ? -95 : 95,
                                                        y: index < 3 ? -55 : 55,
                                                        scale: 0.7
                                                    }
                                            }
                                            animate={
                                                reduceMotion
                                                    ? {}
                                                    : {
                                                        opacity: 1,
                                                        x: 0,
                                                        y: [0, index % 2 === 0 ? -6 : 6, 0],
                                                        scale: 1
                                                    }
                                            }
                                            transition={{
                                                opacity: { duration: 0.6, delay: 0.34 + index * 0.07 },
                                                x: { duration: 0.72, delay: 0.34 + index * 0.07 },
                                                scale: { duration: 0.72, delay: 0.34 + index * 0.07 },
                                                y: {
                                                    duration: 4 + index * 0.28,
                                                    repeat: Infinity,
                                                    ease: "easeInOut",
                                                    delay: 0.7 + index * 0.1
                                                }
                                            }}
                                        >
                                            <Icon size={15} />
                                            <div>
                                                <small>{label}</small>
                                                <strong>{value}</strong>
                                            </div>
                                        </motion.div>
                                    ))}

                                    <span className="ethosino-startup-v6-data-packet ethosino-startup-v6-data-packet--one" />
                                    <span className="ethosino-startup-v6-data-packet ethosino-startup-v6-data-packet--two" />
                                    <span className="ethosino-startup-v6-data-packet ethosino-startup-v6-data-packet--three" />
                                </div>

                                <div className="ethosino-startup-v6-console-status">
                                    <div><span>market</span><code>target audience</code><b>PLAN</b></div>
                                    <div><span>games</span><code>launch selection</code><b>CHOOSE</b></div>
                                    <div><span>providers</span><code>payments + ID</code><b>QUALIFY</b></div>
                                    <div><span>pilot</span><code>success criteria</code><b>DEFINE</b></div>
                                </div>
                            </motion.aside>
                        </div>
                    </motion.div>

                    <div className="ethosino-startup-v6-manifesto-rail">
                        <div className="ethosino-startup-v6-manifesto-track">
                            {[
                                "DEFINE THE AUDIENCE",
                                "QUALIFY THE MARKET",
                                "SELECT THE CATALOG",
                                "PLAN PAYMENT FLOWS",
                                "SET PILOT TARGETS",
                                "SUPPORT REAL PLAYERS",
                                "REVIEW THE SIGNALS",
                                "EXPAND DELIBERATELY",
                                "DEFINE THE AUDIENCE",
                                "QUALIFY THE MARKET",
                                "SELECT THE CATALOG",
                                "PLAN PAYMENT FLOWS",
                                "SET PILOT TARGETS",
                                "SUPPORT REAL PLAYERS",
                                "REVIEW THE SIGNALS",
                                "EXPAND DELIBERATELY"
                            ].map((item, index) => (
                                <React.Fragment key={`${item}-${index}`}>
                                    <span>{item}</span>
                                    <i>◆</i>
                                </React.Fragment>
                            ))}
                        </div>
                    </div>
                </section>

                <nav className="ethosino-startup-v6-subnav" aria-label="Startup operator page navigation">
                    <div className="ethosino-startup-v6-shell ethosino-startup-v6-subnav-inner">
                        <div className="ethosino-startup-v6-subnav-brand">
                            <span /> STARTUP OPERATOR PLAYBOOK
                        </div>

                        <div className="ethosino-startup-v6-subnav-links">
                            <a href="#startup-problem">Challenges</a>
                            <a href="#operator-stack">Essentials</a>
                            <a href="#proof-room">Readiness</a>
                            <a href="#copilot">Review</a>
                            <a href="#launch-path">Pilot</a>
                            <a href="#compare">Options</a>
                            <a href="#faq">FAQ</a>
                            <Link to="/book-demo">Book demo</Link>
                        </div>
                    </div>
                </nav>

                {/* ==========================================================
                    STARTUP PROBLEM
                ========================================================== */}
                <section className="ethosino-startup-v6-section" id="startup-problem">
                    <div className="ethosino-startup-v6-shell">
                        <div className="ethosino-startup-v6-section-head ethosino-startup-v6-section-head--split">
                            <FlyIn direction="bottomLeft">
                                <div>
                                    <span className="ethosino-startup-v6-kicker">THE FIRST-OPERATOR CHALLENGE</span>
                                    <h2>
                                        The website is only one part of launch.
                                        <span> The business has to be ready too.</span>
                                    </h2>
                                </div>
                            </FlyIn>

                            <FlyIn direction="topRight" delay={0.08}>
                                <p>
                                    A first casino has three parallel jobs: find players who want the product, qualify the services and legal basis needed to serve them, and prepare a small team to operate the result. Software helps with the third; it does not remove the first two.
                                </p>
                            </FlyIn>
                        </div>

                        <div className="ethosino-startup-v6-pain-grid">
                            {startupPain.map((item, index) => {
                                const Icon = item.icon;
                                const direction = ["bottomLeft", "topRight", "bottomRight"][index];

                                return (
                                    <FlyIn key={item.number} direction={direction} delay={index * 0.06}>
                                        <SpotlightCard
                                            className={`ethosino-startup-v6-pain-card ethosino-startup-v6-pain-card--${item.accent}`}
                                        >
                                            <div className="ethosino-startup-v6-pain-index">{item.number}</div>
                                            <div className="ethosino-startup-v6-pain-icon"><Icon size={21} /></div>
                                            <h3>{item.title}</h3>
                                            <p>{item.text}</p>
                                        </SpotlightCard>
                                    </FlyIn>
                                );
                            })}
                        </div>

                        <FlyIn direction="bottomRight" delay={0.08}>
                            <SpotlightCard className="ethosino-startup-v6-choice-engine">
                                <div className="ethosino-startup-v6-choice-grid" aria-hidden="true" />

                                <div className="ethosino-startup-v6-choice-copy">
                                    <span className="ethosino-startup-v6-kicker">AN OPERABLE STARTING POINT</span>
                                    <h3>
                                        Keep your attention on <em>finding demand</em> and <em>serving players.</em>
                                    </h3>
                                    <p>
                                        Use an existing product foundation to test the commercial assumptions that matter: audience, offer, first-play activation, retention, payment readiness and support capacity. The founder still makes the business decisions.
                                    </p>
                                </div>

                                <div className="ethosino-startup-v6-choice-visual">
                                    <motion.div
                                        className="ethosino-startup-v6-choice-node ethosino-startup-v6-choice-node--muted"
                                        whileHover={reduceMotion ? {} : { y: -5, rotate: -1 }}
                                    >
                                        <Braces size={21} />
                                        <span>BUILD FROM SCRATCH</span>
                                        <small>Maximum customization; larger initial engineering burden.</small>
                                    </motion.div>

                                    <div className="ethosino-startup-v6-choice-route">
                                        <span />
                                        <ArrowRight size={18} />
                                    </div>

                                    <motion.div
                                        className="ethosino-startup-v6-choice-node ethosino-startup-v6-choice-node--core"
                                        whileHover={reduceMotion ? {} : { y: -7, scale: 1.015 }}
                                    >
                                        <Sparkles size={22} />
                                        <span>ETHOSINO</span>
                                        <small>Existing software, a defined pilot and room to adapt.</small>
                                    </motion.div>

                                    <div className="ethosino-startup-v6-choice-route ethosino-startup-v6-choice-route--reverse">
                                        <ArrowRight size={18} />
                                        <span />
                                    </div>

                                    <motion.div
                                        className="ethosino-startup-v6-choice-node ethosino-startup-v6-choice-node--muted"
                                        whileHover={reduceMotion ? {} : { y: -5, rotate: 1 }}
                                    >
                                        <LockKeyhole size={21} />
                                        <span>BUY A TURNKEY PACKAGE</span>
                                        <small>Compare scope, provider obligations and contract terms.</small>
                                    </motion.div>
                                </div>
                            </SpotlightCard>
                        </FlyIn>
                    </div>
                </section>

                {/* ==========================================================
                    OPERATOR STACK
                ========================================================== */}
                <section
                    className="ethosino-startup-v6-section ethosino-startup-v6-section--panel"
                    id="operator-stack"
                >
                    <div className="ethosino-startup-v6-shell">
                        <div className="ethosino-startup-v6-section-head ethosino-startup-v6-section-head--center">
                            <FlyIn direction="topLeft">
                                <span className="ethosino-startup-v6-kicker">SIX LAUNCH DECISIONS</span>
                                <h2>
                                    Six decisions before your first campaign.
                                    <span> Start with the ones that can block launch.</span>
                                </h2>
                                <p>
                                    Explore the questions every new operator must resolve: how the casino is presented, which games lead the offer, how payments and redemptions work, what approvals are needed, who operates the business and what success should look like.
                                </p>
                            </FlyIn>
                        </div>

                        <div className="ethosino-startup-v6-stack-explorer">
                            <FlyIn direction="bottomLeft" className="ethosino-startup-v6-stack-tabs">
                                <div className="ethosino-startup-v6-stack-tabs-head">
                                    <div>
                                        <span>LAUNCH WORKSTREAMS</span>
                                        <strong>Choose a decision area</strong>
                                    </div>
                                    <div><span /> GUIDE</div>
                                </div>

                                {platformModules.map((item) => {
                                    const Icon = item.icon;
                                    const active = item.id === activeModule;

                                    return (
                                        <motion.button
                                            type="button"
                                            key={item.id}
                                            className={`ethosino-startup-v6-stack-tab ethosino-startup-v6-stack-tab--${item.accent} ${active ? "is-active" : ""}`}
                                            onClick={() => setActiveModule(item.id)}
                                            whileHover={reduceMotion ? {} : { x: 6 }}
                                            whileTap={reduceMotion ? {} : { scale: 0.985 }}
                                        >
                                            <span className="ethosino-startup-v6-stack-tab-number">{item.number}</span>
                                            <span className="ethosino-startup-v6-stack-tab-icon"><Icon size={17} /></span>
                                            <span className="ethosino-startup-v6-stack-tab-copy">
                                                <small>{item.label}</small>
                                                <strong>{item.title}</strong>
                                            </span>
                                            <ArrowRight size={15} />
                                        </motion.button>
                                    );
                                })}
                            </FlyIn>

                            <FlyIn direction="topRight" delay={0.08} className="ethosino-startup-v6-stack-display-wrap">
                                <SpotlightCard
                                    className={`ethosino-startup-v6-stack-display ethosino-startup-v6-stack-display--${selectedModule.accent}`}
                                >
                                    <div className="ethosino-startup-v6-stack-display-grid" aria-hidden="true" />

                                    <AnimatePresence mode="wait">
                                        <motion.div
                                            key={selectedModule.id}
                                            className="ethosino-startup-v6-stack-display-inner"
                                            initial={
                                                reduceMotion
                                                    ? false
                                                    : { opacity: 0, x: 45, y: -25, rotate: 1.5, filter: "blur(7px)" }
                                            }
                                            animate={{ opacity: 1, x: 0, y: 0, rotate: 0, filter: "blur(0px)" }}
                                            exit={
                                                reduceMotion
                                                    ? {}
                                                    : { opacity: 0, x: -35, y: 20, filter: "blur(7px)" }
                                            }
                                            transition={{ duration: 0.34 }}
                                        >
                                            <div className="ethosino-startup-v6-stack-display-top">
                                                <div className="ethosino-startup-v6-stack-display-icon">
                                                    <SelectedModuleIcon size={25} />
                                                </div>
                                                <div>
                                                    <span>{selectedModule.number} / {selectedModule.label}</span>
                                                    <small>STARTUP PLANNING AREA</small>
                                                </div>
                                                <b>PLAN</b>
                                            </div>

                                            <h3>{selectedModule.title}</h3>
                                            <p className="ethosino-startup-v6-stack-short">{selectedModule.short}</p>

                                            <div className="ethosino-startup-v6-stack-telemetry">
                                                {selectedModule.telemetry.map((signal) => {
                                                    const [key, value] = signal.split(" / ");
                                                    return (
                                                        <div key={signal}>
                                                            <span>{key}</span>
                                                            <strong>{value}</strong>
                                                        </div>
                                                    );
                                                })}
                                            </div>

                                            <p className="ethosino-startup-v6-stack-body">{selectedModule.body}</p>

                                            <div className="ethosino-startup-v6-stack-bullets">
                                                {selectedModule.bullets.map((bullet) => (
                                                    <div key={bullet}>
                                                        <span><Check size={12} /></span>
                                                        {bullet}
                                                    </div>
                                                ))}
                                            </div>

                                            <div className="ethosino-startup-v6-stack-footer">
                                                <span>define</span>
                                                <span>prepare</span>
                                                <span>test</span>
                                                <span>learn</span>
                                            </div>
                                        </motion.div>
                                    </AnimatePresence>
                                </SpotlightCard>
                            </FlyIn>
                        </div>
                    </div>
                </section>

                {/* ==========================================================
                    PROOF ROOM
                ========================================================== */}
                <section className="ethosino-startup-v6-section" id="proof-room">
                    <div className="ethosino-startup-v6-shell">
                        <div className="ethosino-startup-v6-section-head ethosino-startup-v6-section-head--split">
                            <FlyIn direction="topRight">
                                <div>
                                    <span className="ethosino-startup-v6-kicker">PILOT READINESS REVIEW</span>
                                    <h2>
                                        Plan the checks before launch.
                                        <span> Know what still needs an owner.</span>
                                    </h2>
                                </div>
                            </FlyIn>

                            <FlyIn direction="bottomLeft" delay={0.08}>
                                <p>
                                    Use these illustrative review panels to think through game acceptance, payment readiness, support escalations and the metrics of a first pilot. They are examples of decisions to validate, not results from a running operator.
                                </p>
                            </FlyIn>
                        </div>

                        <div className="ethosino-startup-v6-proof-room">
                            <FlyIn direction="bottomLeft" className="ethosino-startup-v6-proof-selector">
                                {evidenceModes.map((mode) => {
                                    const Icon = mode.icon;
                                    const active = mode.id === activeEvidence;

                                    return (
                                        <motion.button
                                            type="button"
                                            key={mode.id}
                                            onClick={() => setActiveEvidence(mode.id)}
                                            className={`ethosino-startup-v6-proof-mode ethosino-startup-v6-proof-mode--${mode.accent} ${active ? "is-active" : ""}`}
                                            whileHover={reduceMotion ? {} : { x: 5 }}
                                        >
                                            <span><Icon size={17} /></span>
                                            <div>
                                                <small>{mode.label}</small>
                                                <strong>{mode.title}</strong>
                                            </div>
                                            <ArrowRight size={15} />
                                        </motion.button>
                                    );
                                })}
                            </FlyIn>

                            <FlyIn direction="topRight" delay={0.08} className="ethosino-startup-v6-proof-terminal-wrap">
                                <SpotlightCard
                                    className={`ethosino-startup-v6-proof-terminal ethosino-startup-v6-proof-terminal--${selectedEvidence.accent}`}
                                >
                                    <div className="ethosino-startup-v6-proof-terminal-beam" />
                                    <div className="ethosino-startup-v6-proof-terminal-head">
                                        <div>
                                            <EvidenceIcon size={15} />
                                            pilot.review / {selectedEvidence.id}
                                        </div>
                                        <span>ILLUSTRATIVE</span>
                                    </div>

                                    <AnimatePresence mode="wait">
                                        <motion.div
                                            key={selectedEvidence.id}
                                            className="ethosino-startup-v6-proof-terminal-content"
                                            initial={reduceMotion ? false : { opacity: 0, y: 22, x: 35 }}
                                            animate={{ opacity: 1, y: 0, x: 0 }}
                                            exit={reduceMotion ? {} : { opacity: 0, y: -15, x: -25 }}
                                            transition={{ duration: 0.3 }}
                                        >
                                            <span className="ethosino-startup-v6-proof-terminal-label">{selectedEvidence.label}</span>
                                            <h3>{selectedEvidence.title}</h3>
                                            <p>{selectedEvidence.copy}</p>

                                            <div className="ethosino-startup-v6-proof-rows">
                                                {selectedEvidence.rows.map(([key, value, status]) => (
                                                    <div key={key}>
                                                        <span>{key}</span>
                                                        <code>{value}</code>
                                                        <b>{status}</b>
                                                    </div>
                                                ))}
                                            </div>

                                            <div className="ethosino-startup-v6-proof-pass">
                                                <span><BadgeCheck size={17} /></span>
                                                <div>
                                                    <small>STARTUP CHECKLIST</small>
                                                    <strong>Example workflow / verify during pilot</strong>
                                                </div>
                                                <b>SAMPLE</b>
                                            </div>
                                        </motion.div>
                                    </AnimatePresence>
                                </SpotlightCard>
                            </FlyIn>
                        </div>
                    </div>
                </section>

                {/* ==========================================================
                    FIRST-PARTY PRODUCT
                ========================================================== */}
                <section className="ethosino-startup-v6-section ethosino-startup-v6-section--angled">
                    <div className="ethosino-startup-v6-shell ethosino-startup-v6-first-party-grid">
                        <FlyIn direction="topLeft">
                            <div className="ethosino-startup-v6-first-party-copy">
                                <span className="ethosino-startup-v6-kicker">BUILD A FIRST-PLAY REASON</span>
                                <h2>
                                    Your first catalog should have a point of view.
                                    <span> Not just a long list of games.</span>
                                </h2>
                                <p>
                                    Start with a deliberately chosen mix: a recognizable table game for credibility, a fast original for short sessions and a few distinctive experiences worth returning for. Use actual player activity to learn which categories justify more investment.
                                </p>

                                <div className="ethosino-startup-v6-first-party-points">
                                    <div><Gamepad2 size={17} /><span><strong>Available originals and tables</strong> let you curate instead of launching an empty lobby.</span></div>
                                    <div><Fingerprint size={17} /><span><strong>Named launch categories</strong> make acquisition messages more specific and testable.</span></div>
                                    <div><Radio size={17} /><span><strong>Scheduled tournament experiences</strong> can support planned events when operationally ready.</span></div>
                                    <div><Network size={17} /><span><strong>Catalog expansion follows evidence</strong> rather than being the first spending priority.</span></div>
                                </div>
                            </div>
                        </FlyIn>

                        <FlyIn direction="bottomRight" delay={0.09}>
                            <SpotlightCard className="ethosino-startup-v6-game-constellation">
                                <div className="ethosino-startup-v6-game-constellation-grid" />
                                <div className="ethosino-startup-v6-game-core">
                                    <Gamepad2 size={24} />
                                    <span>FIRST-PARTY</span>
                                    <strong>CATALOG</strong>
                                </div>

                                {[
                                    ["ORIGINALS", "SELECT", "one"],
                                    ["TABLES", "FAMILIAR", "two"],
                                    ["POKER", "EVENTS", "three"],
                                    ["RETENTION", "MEASURE", "four"],
                                    ["CATALOG", "ITERATE", "five"]
                                ].map(([label, value, position], index) => (
                                    <motion.div
                                        key={label}
                                        className={`ethosino-startup-v6-game-satellite ethosino-startup-v6-game-satellite--${position}`}
                                        animate={
                                            reduceMotion
                                                ? {}
                                                : {
                                                    y: [0, index % 2 === 0 ? -8 : 8, 0],
                                                    x: [0, index % 2 === 0 ? 3 : -3, 0]
                                                }
                                        }
                                        transition={{
                                            duration: 4.2 + index * 0.35,
                                            repeat: Infinity,
                                            ease: "easeInOut"
                                        }}
                                    >
                                        <small>{label}</small>
                                        <strong>{value}</strong>
                                    </motion.div>
                                ))}

                                <span className="ethosino-startup-v6-game-packet ethosino-startup-v6-game-packet--one" />
                                <span className="ethosino-startup-v6-game-packet ethosino-startup-v6-game-packet--two" />
                            </SpotlightCard>
                        </FlyIn>
                    </div>
                </section>

                {/* ==========================================================
                    AI COPILOT
                ========================================================== */}
                <section className="ethosino-startup-v6-section" id="copilot">
                    <div className="ethosino-startup-v6-shell">
                        <FlyIn direction="bottomRight">
                            <SpotlightCard className="ethosino-startup-v6-copilot-shell">
                                <div className="ethosino-startup-v6-copilot-stars" aria-hidden="true" />

                                <div className="ethosino-startup-v6-copilot-copy">
                                    <span className="ethosino-startup-v6-kicker ethosino-startup-v6-kicker--pink">WEEKLY OPERATOR REVIEW</span>
                                    <h2>
                                        Use the first week's data.
                                        <span> Decide what to fix in the second.</span>
                                    </h2>
                                    <p>
                                        A small team cannot investigate everything at once. Review registration drop-off, popular games, support backlogs and payment exceptions on a regular cadence. The operator Copilot can help organize permitted data for a human review; the business still decides which changes to make.
                                    </p>

                                    <div className="ethosino-startup-v6-copilot-rule">
                                        <Bot size={20} />
                                        <div>
                                            <small>PRACTICAL RULE</small>
                                            <strong>Treat summaries as leads to investigate, not decisions to automate.</strong>
                                        </div>
                                    </div>

                                    <div className="ethosino-startup-v6-copilot-denials">
                                        <span><KeyRound size={13} /> no automatic policy changes</span>
                                        <span><WalletCards size={13} /> no payout approval</span>
                                        <span><Gamepad2 size={13} /> no game result control</span>
                                        <span><ShieldCheck size={13} /> no substitute for the operator</span>
                                    </div>
                                </div>

                                <div className="ethosino-startup-v6-copilot-console">
                                    <div className="ethosino-startup-v6-copilot-console-head">
                                        <div><span /> WEEKLY BRIEF / EXAMPLE</div>
                                        <b>SAMPLE</b>
                                    </div>

                                    <div className="ethosino-startup-v6-copilot-scope">
                                        <span>VISITS</span>
                                        <span>SIGNUPS</span>
                                        <span>FIRST PLAY</span>
                                        <span>RETURNING</span>
                                        <span>SUPPORT</span>
                                        <span>PAYMENTS</span>
                                    </div>

                                    <label className="ethosino-startup-v6-copilot-input">
                                        <Search size={15} />
                                        <input
                                            value={copilotPrompt}
                                            onChange={(event) => setCopilotPrompt(event.target.value)}
                                            aria-label="Example operator copilot query"
                                        />
                                        <button type="button" onClick={runCopilot} disabled={copilotRunning}>
                                            {copilotRunning ? <Activity size={15} /> : <ArrowRight size={15} />}
                                        </button>
                                    </label>

                                    <div className="ethosino-startup-v6-copilot-response">
                                        <AnimatePresence mode="wait">
                                            {copilotRunning ? (
                                                <motion.div
                                                    key="loading"
                                                    className="ethosino-startup-v6-copilot-thinking"
                                                    initial={{ opacity: 0 }}
                                                    animate={{ opacity: 1 }}
                                                    exit={{ opacity: 0 }}
                                                >
                                                    <span /><span /><span />
                                                    Preparing an illustrative example…
                                                </motion.div>
                                            ) : (
                                                <motion.div
                                                    key="result"
                                                    initial={reduceMotion ? false : { opacity: 0, x: 25, y: 10 }}
                                                    animate={{ opacity: 1, x: 0, y: 0 }}
                                                >
                                                    <div className="ethosino-startup-v6-copilot-response-label">
                                                        <Sparkles size={13} /> ILLUSTRATIVE WEEKLY BRIEF
                                                    </div>
                                                    <p>
                                                        Example only: registration is attracting visits, but fewer users reach their first game than expected. Before increasing advertising spend, inspect the account-to-play flow, compare desktop and mobile completion, and test one onboarding change. This panel does not execute the entered query.
                                                    </p>
                                                    <div className="ethosino-startup-v6-copilot-evidence">
                                                        <span>illustrative data</span>
                                                        <span>no live query</span>
                                                        <span>human decision</span>
                                                    </div>
                                                </motion.div>
                                            )}
                                        </AnimatePresence>
                                    </div>
                                </div>
                            </SpotlightCard>
                        </FlyIn>
                    </div>
                </section>

                {/* ==========================================================
                    LAUNCH PATH
                ========================================================== */}
                <section
                    className="ethosino-startup-v6-section ethosino-startup-v6-section--panel"
                    id="launch-path"
                >
                    <div className="ethosino-startup-v6-shell">
                        <div className="ethosino-startup-v6-section-head ethosino-startup-v6-section-head--split">
                            <FlyIn direction="bottomLeft">
                                <div>
                                    <span className="ethosino-startup-v6-kicker">FOUR STEPS TO A MEASURABLE PILOT</span>
                                    <h2>
                                        Get ready to test.
                                        <span> Decide with data afterward.</span>
                                    </h2>
                                </div>
                            </FlyIn>

                            <FlyIn direction="topRight" delay={0.08}>
                                <p>
                                    The pilot is a sequence of business decisions, not simply a deployment milestone. Set the offer, qualify the external dependencies, test a controlled experience and review explicit measures before committing more capital.
                                </p>
                            </FlyIn>
                        </div>

                        <div className="ethosino-startup-v6-runway">
                            <div className="ethosino-startup-v6-runway-line" aria-hidden="true">
                                <span />
                            </div>

                            {launchStages.map((stage, index) => {
                                const Icon = stage.icon;
                                const directions = ["topLeft", "bottomLeft", "topRight", "bottomRight"];

                                return (
                                    <FlyIn
                                        key={stage.number}
                                        direction={directions[index]}
                                        delay={index * 0.08}
                                    >
                                        <SpotlightCard className="ethosino-startup-v6-runway-card">
                                            <div className="ethosino-startup-v6-runway-number">{stage.number}</div>
                                            <div className="ethosino-startup-v6-runway-icon"><Icon size={21} /></div>
                                            <span>{stage.kicker}</span>
                                            <h3>{stage.title}</h3>
                                            <p>{stage.copy}</p>
                                        </SpotlightCard>
                                    </FlyIn>
                                );
                            })}
                        </div>

                        <FlyIn direction="bottomRight" delay={0.12}>
                            <div className="ethosino-startup-v6-pilot-strip">
                                <div className="ethosino-startup-v6-pilot-icon"><Zap size={22} /></div>
                                <div>
                                    <small>SELECTIVE STARTUP PILOT</small>
                                    <strong>A pilot should answer specific questions before you commit to a larger launch.</strong>
                                    <p>
                                        We can scope a focused software pilot around the intended audience and launch offering. Deliverables, duration, integrations, support responsibilities and any commercial terms should be agreed in writing before work begins.
                                    </p>
                                </div>
                                <Link className="ethosino-startup-v6-btn ethosino-startup-v6-btn--primary" to="/book-demo">
                                    Discuss pilot fit <ArrowRight size={16} />
                                </Link>
                            </div>
                        </FlyIn>
                    </div>
                </section>

                {/* ==========================================================
                    COMPARISON
                ========================================================== */}
                <section className="ethosino-startup-v6-section" id="compare">
                    <div className="ethosino-startup-v6-shell">
                        <div className="ethosino-startup-v6-section-head ethosino-startup-v6-section-head--center">
                            <FlyIn direction="topRight">
                                <span className="ethosino-startup-v6-kicker">COMPARE YOUR REAL OPTIONS</span>
                                <h2>
                                    Choose the model that fits your stage.
                                    <span> Compare obligations, not slogans.</span>
                                </h2>
                                <p>
                                    A custom build, packaged white label and Ethosino pilot each carry different trade-offs in engineering effort, commercial flexibility and launch responsibilities. Use the table to decide which work your team can afford and which responsibilities remain yours.
                                </p>
                            </FlyIn>
                        </div>

                        <FlyIn direction="bottomLeft" delay={0.08}>
                            <div className="ethosino-startup-v6-comparison">
                                <div className="ethosino-startup-v6-comparison-head">
                                    <div>DIMENSION</div>
                                    <div>CUSTOM BUILD</div>
                                    <div>PACKAGED WHITE LABEL</div>
                                    <div className="is-ethosino">ETHOSINO</div>
                                </div>

                                {comparisonRows.map(([label, custom, legacy, ethosino]) => (
                                    <div className="ethosino-startup-v6-comparison-row" key={label}>
                                        <strong>{label}</strong>
                                        <span>{custom}</span>
                                        <span>{legacy}</span>
                                        <span className="is-ethosino"><Check size={13} /> {ethosino}</span>
                                    </div>
                                ))}
                            </div>
                        </FlyIn>

                        <div className="ethosino-startup-v6-economics-grid">
                            <FlyIn direction="bottomLeft">
                                <SpotlightCard className="ethosino-startup-v6-economics-card">
                                    <span>01 / FOUNDER TIME</span>
                                    <h3>Spend the first months learning whether players want your offer.</h3>
                                    <p>
                                        A founder may get a better early return from talking to prospective players, testing messages, recruiting affiliates and measuring activation than from commissioning custom software for every operational need.
                                    </p>
                                </SpotlightCard>
                            </FlyIn>

                            <FlyIn direction="topRight" delay={0.06}>
                                <SpotlightCard className="ethosino-startup-v6-economics-card">
                                    <span>02 / COST DISCIPLINE</span>
                                    <h3>Budget for the costs that sit outside the platform.</h3>
                                    <p>
                                        Legal advice, payment processing, identity services, domain and brand work, marketing and customer support can all affect runway. Ask for clear written boundaries around what is included in the software agreement.
                                    </p>
                                </SpotlightCard>
                            </FlyIn>

                            <FlyIn direction="bottomRight" delay={0.12}>
                                <SpotlightCard className="ethosino-startup-v6-economics-card">
                                    <span>03 / DECISION POINT</span>
                                    <h3>Make the expansion decision after the pilot review.</h3>
                                    <p>
                                        Agree on thresholds for engagement, support response, operational readiness and acquisition economics. If those thresholds are not met, refine the offer before committing to more traffic, providers or brands.
                                    </p>
                                </SpotlightCard>
                            </FlyIn>
                        </div>
                    </div>
                </section>

                {/* ==========================================================
                    LEGAL BOUNDARY
                ========================================================== */}
                <section className="ethosino-startup-v6-section ethosino-startup-v6-section--tight">
                    <div className="ethosino-startup-v6-shell">
                        <FlyIn direction="topLeft">
                            <SpotlightCard className="ethosino-startup-v6-legal">
                                <div className="ethosino-startup-v6-legal-icon"><Scale size={25} /></div>
                                <div>
                                    <span className="ethosino-startup-v6-kicker ethosino-startup-v6-kicker--amber">
                                        BEFORE PRODUCTION LAUNCH
                                    </span>
                                    <h2>Production readiness requires more than working software.</h2>
                                    <p>
                                        Before accepting real players, your company needs jurisdiction-specific legal advice, suitable payment and identity providers, sweepstakes and promotion rules where applicable, player disclosures, redemption procedures and an accountable operating team. Ethosino does not grant legal authority or guarantee partner approval.
                                    </p>
                                </div>
                                <Link to="/book-demo">
                                    Review launch prerequisites <ArrowRight size={14} />
                                </Link>
                            </SpotlightCard>
                        </FlyIn>
                    </div>
                </section>

                {/* ==========================================================
                    FAQ
                ========================================================== */}
                <section className="ethosino-startup-v6-section" id="faq">
                    <div className="ethosino-startup-v6-shell ethosino-startup-v6-faq-layout">
                        <FlyIn direction="bottomLeft">
                            <div className="ethosino-startup-v6-faq-copy">
                                <span className="ethosino-startup-v6-kicker">STARTUP LAUNCH QUESTIONS</span>
                                <h2>
                                    Know what you are buying.
                                    <span> Know what you still need to provide.</span>
                                </h2>
                                <p>
                                    These are the questions we'd settle before a pilot: who brings the audience, what approvals are outstanding, how success is measured, what the software includes and what the operating team must handle itself.
                                </p>

                                <div className="ethosino-startup-v6-faq-callout">
                                    <Radio size={18} />
                                    <div>
                                        <strong>Bring your launch assumptions.</strong>
                                        <span>A useful demo starts with your target audience, market and timeline.</span>
                                    </div>
                                    <Link to="/book-demo"><ArrowRight size={16} /></Link>
                                </div>
                            </div>
                        </FlyIn>

                        <div className="ethosino-startup-v6-faq-list">
                            {faqItems.map((item, index) => {
                                const open = activeFaq === index;
                                const direction = index % 2 === 0 ? "topRight" : "bottomRight";

                                return (
                                    <FlyIn key={item.q} direction={direction} delay={Math.min(index * 0.03, 0.16)}>
                                        <div className={`ethosino-startup-v6-faq-item ${open ? "is-open" : ""}`}>
                                            <button
                                                type="button"
                                                onClick={() => setActiveFaq(open ? -1 : index)}
                                                aria-expanded={open}
                                            >
                                                <span>{String(index + 1).padStart(2, "0")}</span>
                                                <strong>{item.q}</strong>
                                                <motion.i animate={{ rotate: open ? 180 : 0 }}>
                                                    <ChevronDown size={17} />
                                                </motion.i>
                                            </button>

                                            <AnimatePresence initial={false}>
                                                {open && (
                                                    <motion.div
                                                        className="ethosino-startup-v6-faq-answer"
                                                        initial={{ height: 0, opacity: 0 }}
                                                        animate={{ height: "auto", opacity: 1 }}
                                                        exit={{ height: 0, opacity: 0 }}
                                                        transition={{ duration: 0.28 }}
                                                    >
                                                        <p>{item.a}</p>
                                                    </motion.div>
                                                )}
                                            </AnimatePresence>
                                        </div>
                                    </FlyIn>
                                );
                            })}
                        </div>
                    </div>
                </section>

                {/* ==========================================================
                    FINAL CTA
                ========================================================== */}
                <section className="ethosino-startup-v6-section ethosino-startup-v6-final">
                    <div className="ethosino-startup-v6-shell">
                        <FlyIn direction="bottomRight">
                            <SpotlightCard className="ethosino-startup-v6-final-card">
                                <div className="ethosino-startup-v6-final-grid" aria-hidden="true" />
                                <div className="ethosino-startup-v6-final-orbit" aria-hidden="true" />

                                <div className="ethosino-startup-v6-final-copy">
                                    <span className="ethosino-startup-v6-kicker">
                                        FOR FOUNDERS PREPARING THEIR FIRST LAUNCH
                                    </span>
                                    <h2>
                                        Bring your launch hypothesis.
                                        <span> Leave with the next decisions clearer.</span>
                                    </h2>
                                    <p>
                                        Tell us who you intend to serve, where you plan to operate, how you expect to acquire players and which partners are already in place. We can examine whether a focused Ethosino pilot makes sense, identify the open dependencies and agree on the software scope to evaluate.
                                    </p>

                                    <div className="ethosino-startup-v6-final-actions">
                                        <Link
                                            className="ethosino-startup-v6-btn ethosino-startup-v6-btn--primary ethosino-startup-v6-btn--large"
                                            to="/book-demo"
                                        >
                                            <Rocket size={17} />
                                            Discuss my startup plan
                                            <ArrowRight size={17} />
                                        </Link>

                                        <Link
                                            className="ethosino-startup-v6-btn ethosino-startup-v6-btn--ghost ethosino-startup-v6-btn--large"
                                            to="/docs"
                                        >
                                            <TerminalSquare size={16} />
                                            Explore technical material
                                        </Link>
                                    </div>
                                </div>

                                <div className="ethosino-startup-v6-final-checks">
                                    {[
                                        "Define the intended player audience",
                                        "Select markets and legal advisers",
                                        "Choose a sensible first catalog",
                                        "Qualify payment and identity partners",
                                        "Set pilot acceptance criteria",
                                        "Assign operating and support owners"
                                    ].map((item, index) => (
                                        <motion.div
                                            key={item}
                                            initial={reduceMotion ? false : { opacity: 0, x: 55, y: index % 2 ? 15 : -15 }}
                                            whileInView={{ opacity: 1, x: 0, y: 0 }}
                                            viewport={{ once: true }}
                                            transition={{ delay: 0.08 + index * 0.05 }}
                                        >
                                            <span><Check size={13} /></span>
                                            {item}
                                        </motion.div>
                                    ))}
                                </div>
                            </SpotlightCard>
                        </FlyIn>
                    </div>
                </section>
            </main>

            <Footer />
        </>
    );
};

export default StartupOperatorsSolutionHelper;
